import { AuthProviderProps, UserCredentials } from '@ais-ui/auth';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { LoginCard } from './LoginCard';
import { RegistrationCard } from './RegistrationCard';
import { SuccessfulRegistrationCard } from './SuccessfulRegistrationCard';

interface AuthProviderContextState {
  credentials?: UserCredentials;
  showLogin: boolean;
  setShowLogin: (showLogin: boolean) => void;
  showRegistration: boolean;
  setShowRegistration: (showLogin: boolean) => void;
  showRegistrationSuccess: boolean;
  setShowRegistrationSuccess: (showLogin: boolean) => void;
  login: (credentials: UserCredentials) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthProviderContextState | undefined>(
  undefined
);

export const LoginProvider = ({
  children,
  initialCredentials,
}: AuthProviderProps) => {
  const [credentials, setCredentials] = useState<UserCredentials | undefined>(
    () => initialCredentials ?? readFromLocalStorage()
  );
  const [showLogin, setShowLogin] = useState<boolean>(false);
  const [showRegistration, setShowRegistration] = useState<boolean>(false);
  const [showRegistrationSuccess, setShowRegistrationSuccess] =
    useState<boolean>(false);
  const value = useMemo(
    () => ({
      credentials,
      login: (credentials: UserCredentials) => {
        setCredentials(credentials);
        storeInLocalStorage(credentials);
        setShowLogin(false);
      },
      logout: () => {
        setCredentials(undefined);
        clearUseData();
      },
      showLogin: showLogin,
      setShowLogin: setShowLogin,
      showRegistration: showRegistration,
      setShowRegistration: setShowRegistration,
      showRegistrationSuccess: showRegistrationSuccess,
      setShowRegistrationSuccess: setShowRegistrationSuccess,
    }),
    [credentials, showLogin, showRegistration, showRegistrationSuccess]
  );

  return (
    <AuthContext.Provider value={value}>
      <>
        {showLogin && !showRegistration && <LoginCard />}{' '}
        {showRegistration && <RegistrationCard />}
        {!showRegistration && showRegistrationSuccess && (
          <SuccessfulRegistrationCard />
        )}{' '}
        {children}
      </>
    </AuthContext.Provider>
  );
};

const readFromLocalStorage = () => {
  const tempCredentials = localStorage.getItem('credentials');
  return tempCredentials != null ? JSON.parse(tempCredentials) : undefined;
};

const storeInLocalStorage = (credentials: UserCredentials) => {
  localStorage.setItem('credentials', JSON.stringify(credentials));
};

const clearUseData = () => {
  localStorage.removeItem('credentials');
  localStorage.removeItem('access_token');
};

export const useLogin = () => {
  const authContext = useContext(AuthContext);

  if (!authContext) {
    throw new Error(
      'useOceanIOAuth must be used within an OceanIOAuthProvider'
    );
  }

  return authContext;
};

export const useCredentials = () => {
  const authContext = useLogin();

  const { credentials } = authContext;

  if (credentials == null) {
    throw new Error('useCredentials must be used after user is logged in');
  }

  return credentials;
};
